import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Col, Row, Space, Spin, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FeedbackCard } from "../../components/FeedbackCard";
import { CarouselPage } from "../../components/CarouselPage";
import { ProjectCard } from "../../components/ProjectCard";
import styles from "./Project.module.scss";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { ApiContext } from "../../components/Routing/ApiContext";
import { useContext } from "react";

const { Title, Paragraph } = Typography;

export const Project: React.FC = () => {
  const { t } = useTranslation();
  const [project, setProject] = useState<Project>({} as Project);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const { projectURL } = useParams();
  const api = useContext(ApiContext)?.api;

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${api}/projects/${projectURL}`
      );
      const project = response.data;
      setProject(project);
      return project;
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (type: Array<string>, id: Project["_id"]) => {
    setLoadingProjects(true);
    try {
      const response = await axios.get(
        `${api}/projects`,
        {
          params: {
            filters: {
              type,
            },
          },
          withCredentials: true,
        }
      );
      const projects = response.data;
      const shuffledProjects = projects
        .filter((item: Project) => item._id !== id)
        .sort(() => Math.random() - 0.5);

      setProjects(shuffledProjects); 
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    fetchProject().then((res) => {
      fetchProjects(res?.type, res?._id);
    });
  }, [projectURL]);

  if (!project._id) {
    return (
      <div className={styles.noProjectsBlock}>
        <div>{t("portfolio.noProjects")}</div>
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          size="5x"
          style={{ color: "#c0c0c0", marginTop: 25 }}
        />
      </div>
    );
  }

  return (
    <Spin spinning={loading}>
      <div className={styles.projectDescription}>
        <div className={styles.projectDescriptionContainer}>
          <div className={styles.title}>{project.title}</div>
          <Title level={4} style={{ color: "#fff" }}>
            {t("portfolio.aboutProject")}
          </Title>
          <Paragraph className={styles.text}>
            <span
              dangerouslySetInnerHTML={{
                __html: project.description[i18n.language],
              }}
            />
          </Paragraph>
          <Title level={4} style={{ color: "#fff" }}>
            {t("portfolio.teamContribution")}
          </Title>
          <Paragraph className={styles.text}>
            <span
              dangerouslySetInnerHTML={{
                __html: project.teamContribution[i18n.language],
              }}
            />
          </Paragraph>
          <Title level={4} style={{ color: "#fff" }}>
            {t("portfolio.stack")}
          </Title>
          <div className={styles.tagsWrapper}>
            {project.stack?.map((v) => (
              <div className={styles.tag} key={v}>
                {v}
              </div>
            ))}
          </div>
        </div>
      </div>
      {project.images && <CarouselPage images={project.images} />}

      {!!project.feedback?.length && (
        <div className={styles.feedbackBlock}>
          <div className={styles.feedbackBlockContainer}>
            <div style={{ textAlign: "center" }}>
              <div className={styles.feedbackTitle}>
                {t("portfolio.feedbackTitle")}
              </div>
              <Title level={5}>{t("portfolio.feedbackText")}</Title>
            </div>
            <Space direction="vertical" size="middle">
              {project.feedback?.map((item) => (
                <FeedbackCard data={item} key={item.comment[i18n.language]} />
              ))}
            </Space>
          </div>
        </div>
      )}

      <Spin spinning={loadingProjects}>
        {!!projects.length && (
          <div className={styles.moreProjectsBlock}>
            <Row className={styles.moreProjectsBlockContainer}>
              <Col xs={24} lg={8}>
                <div className={styles.wordMore}>{t("portfolio.more")}</div>
                <div className={styles.wordProjects}>
                  {t("portfolio.projects")}
                </div>
              </Col>
              <Col xs={24} lg={16}>
                <Row gutter={[24, 24]}>
                  {projects.map((item, index) => {
                    if (index < 2) {
                      return (
                        <Col xs={24} lg={12} key={item._id}>
                          <ProjectCard key={project._id} project={item} />
                        </Col>
                      );
                    }
                  })}
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </Spin>
    </Spin>
  );
}
